import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, keyframes } from '@mui/material';
import {
  Area as RechartsArea,
  AreaChart,
  Bar as RechartsBar,
  BarChart,
} from 'recharts';

import { useChartContext } from '../../../ChartProvider';
import { tokens } from '../../../../../theme/generated/tokens';

const BAR_WIDTH = 24;
const BAR_GAP = 28;
const AREA_POINT_SPACING = 40;

const HEIGHT_PATTERN = [
  75, 92, 60, 98, 82, 70, 95, 78, 88, 65, 96, 58, 90, 76, 99, 62,
];

const shimmer = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const generateFakeData = (count: number) =>
  Array.from({ length: count }, (_, i) => ({
    index: i,
    value: HEIGHT_PATTERN[i % HEIGHT_PATTERN.length],
  }));

const getBarCount = (width: number) =>
  Math.max(1, Math.floor(width / (BAR_WIDTH + BAR_GAP)));

const getAreaPointCount = (width: number) =>
  Math.max(2, Math.floor(width / AREA_POINT_SPACING));

type ChartMargin = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

type LoadingOverlayProps = {
  margin?: ChartMargin;
  testId?: string;
};

export const LoadingOverlay = ({ margin, testId }: LoadingOverlayProps) => {
  const chartColor = tokens.theme.bg.loading.skeleton;

  const { chartType, chartBlocks } = useChartContext();
  const variant = chartType === 'bar' || chartBlocks.bar > 0 ? 'bar' : 'area';

  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { width, height } = size;

  const pointCount = useMemo(() => {
    if (!width) return 0;
    return variant === 'bar' ? getBarCount(width) : getAreaPointCount(width);
  }, [width, variant]);

  const fakeData = useMemo(() => generateFakeData(pointCount), [pointCount]);

  return (
    <Box
      ref={containerRef}
      data-testid={testId}
      sx={{
        position: 'relative',
        flex: 1,
        width: '100%',
        animation: `${shimmer} 1.5s ease-in-out infinite`,
      }}
    >
      {width > 0 &&
        (variant === 'bar' ? (
          <BarChart
            width={width}
            height={height}
            data={fakeData}
            margin={margin}
          >
            <RechartsBar
              dataKey="value"
              fill={chartColor}
              barSize={BAR_WIDTH}
              isAnimationActive={false}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        ) : (
          <AreaChart
            width={width}
            height={height}
            data={fakeData}
            margin={margin}
          >
            <RechartsArea
              dataKey="value"
              fill={chartColor}
              stroke="none"
              type="monotone"
              isAnimationActive={false}
            />
          </AreaChart>
        ))}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: `linear-gradient(to top, ${tokens.theme.bg.structural.surface}, rgba(255,255,255,0))`,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};
